import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import type {
  ApiExternalFramework,
  ApiExternalFrameworkClause,
  ApiPractice,
  ApiPracticeArea,
  GetContentBundleResponse,
} from '@/types/api'
import { TreeChevronIcon } from '@/components/TreeChevronIcon'
import { GuardedLink } from './GuardedLink'

interface FrameworkClauseTreeProps {
  framework: ApiExternalFramework
  bundle: GetContentBundleResponse
}

interface MappingRow {
  mappingId: number
  practice: ApiPractice
  practiceArea: ApiPracticeArea | undefined
  relationshipType: number
  note?: string | null
}

interface ClauseNode {
  clause: ApiExternalFrameworkClause
  children: ClauseNode[]
  mappings: MappingRow[]
}

function compareClauseKeys(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

export function FrameworkClauseTree({ framework, bundle }: FrameworkClauseTreeProps) {
  const { t } = useTranslation()

  const { roots, expandableIds } = useMemo(() => {
    const practiceMap = new Map<number, ApiPractice>(bundle.practices.map((p) => [p.id, p]))
    const practiceAreaMap = new Map<number, ApiPracticeArea>(
      (bundle.practiceAreas ?? []).map((pa) => [pa.id, pa]),
    )

    const clauses = (bundle.externalFrameworkClauses ?? []).filter(
      (c) => c.externalFrameworkId === framework.id,
    )
    const clauseIds = new Set(clauses.map((c) => c.id))

    // Group mappings by clause.
    const mappingsByClause = new Map<number, MappingRow[]>()
    for (const m of bundle.practiceCrosswalkMappings ?? []) {
      if (m.externalFrameworkId !== framework.id) continue
      if (!clauseIds.has(m.externalFrameworkClauseId)) continue
      const practice = practiceMap.get(m.practiceId)
      if (!practice) continue
      let rows = mappingsByClause.get(m.externalFrameworkClauseId)
      if (!rows) {
        rows = []
        mappingsByClause.set(m.externalFrameworkClauseId, rows)
      }
      rows.push({
        mappingId: m.id,
        practice,
        practiceArea: practiceAreaMap.get(practice.practiceAreaId),
        relationshipType: m.relationshipType,
        note: m.note,
      })
    }
    for (const rows of mappingsByClause.values()) {
      rows.sort(
        (a, b) =>
          (a.practiceArea?.sortOrder ?? 0) - (b.practiceArea?.sortOrder ?? 0) ||
          a.practice.sortOrder - b.practice.sortOrder,
      )
    }

    // Build the node index, then wire parent/child links. A parent that isn't
    // part of this framework (or is missing) promotes the clause to a root.
    const nodeMap = new Map<number, ClauseNode>()
    for (const clause of clauses) {
      nodeMap.set(clause.id, {
        clause,
        children: [],
        mappings: mappingsByClause.get(clause.id) ?? [],
      })
    }

    const rootNodes: ClauseNode[] = []
    for (const node of nodeMap.values()) {
      const parentId = node.clause.parentClauseId
      const parent = parentId != null ? nodeMap.get(parentId) : undefined
      if (parent) parent.children.push(node)
      else rootNodes.push(node)
    }

    const sortNodes = (nodes: ClauseNode[]) => {
      nodes.sort((a, b) => compareClauseKeys(a.clause.clauseKey, b.clause.clauseKey))
      for (const n of nodes) sortNodes(n.children)
    }
    sortNodes(rootNodes)

    // Clause hierarchy is always shown; only a clause's own CMMI mappings collapse.
    // Collect the ids of every clause that actually has mappings to toggle.
    const expandable = new Set<number>()
    const collect = (node: ClauseNode) => {
      if (node.mappings.length > 0) expandable.add(node.clause.id)
      for (const child of node.children) collect(child)
    }
    for (const node of rootNodes) collect(node)

    return { roots: rootNodes, expandableIds: expandable }
  }, [bundle, framework.id])

  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set(expandableIds))

  const toggle = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => setExpandedIds(new Set(expandableIds))
  const collapseAll = () => setExpandedIds(new Set())

  if (roots.length === 0) {
    return <p className="empty-section">{t('content.frameworkTreeNoClauses')}</p>
  }

  return (
    <div className="framework-tree">
      <div className="framework-tree-toolbar">
        <button type="button" className="framework-tree-toolbar-btn" onClick={expandAll}>
          {t('content.frameworkTreeExpandAll')}
        </button>
        <span className="framework-tree-toolbar-sep" aria-hidden="true">
          ·
        </span>
        <button type="button" className="framework-tree-toolbar-btn" onClick={collapseAll}>
          {t('content.frameworkTreeCollapseAll')}
        </button>
      </div>
      <div className="framework-tree-root" role="tree" aria-label={framework.name}>
        {roots.map((node) => (
          <TreeNode
            key={node.clause.id}
            node={node}
            depth={0}
            expandedIds={expandedIds}
            onToggle={toggle}
          />
        ))}
      </div>
    </div>
  )
}

interface TreeNodeProps {
  node: ClauseNode
  depth: number
  expandedIds: Set<number>
  onToggle: (id: number) => void
}

function TreeNode({ node, depth, expandedIds, onToggle }: TreeNodeProps) {
  const { t } = useTranslation()
  const hasChildren = node.children.length > 0
  const hasMappings = node.mappings.length > 0
  const isExpanded = expandedIds.has(node.clause.id)
  const isRoot = depth === 0

  return (
    <div className="framework-tree-node" role="treeitem" aria-expanded={hasMappings ? isExpanded : undefined}>
      <div
        className={`framework-tree-row${isRoot ? ' framework-tree-row--root' : ''}${hasMappings ? ' framework-tree-row--expandable' : ''}`}
        tabIndex={hasMappings ? 0 : -1}
        onClick={() => hasMappings && onToggle(node.clause.id)}
        onKeyDown={(e) => {
          if (hasMappings && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            onToggle(node.clause.id)
          }
        }}
      >
        {hasMappings ? (
          <span
            className={`framework-tree-chevron${isExpanded ? ' framework-tree-chevron--open' : ''}`}
            aria-hidden="true"
          >
            <TreeChevronIcon />
          </span>
        ) : (
          <span className="framework-tree-leaf-dot" aria-hidden="true" />
        )}
        <code className="crosswalk-clause-key">{node.clause.clauseKey}</code>
        <span className="crosswalk-clause-topic">{node.clause.topic}</span>
        {hasMappings && (
          <span className="framework-tree-count">
            {t('content.frameworkTreeMappings', { count: node.mappings.length })}
          </span>
        )}
      </div>

      {/* A clause's own CMMI mappings — the only collapsible part of the tree */}
      {hasMappings && isExpanded && (
        <div className="framework-tree-children framework-tree-mappings-group" role="group">
          {node.mappings.map((row) => (
            <MappingLeaf key={row.mappingId} row={row} />
          ))}
        </div>
      )}

      {/* Child clauses are always shown — the clause hierarchy never collapses */}
      {hasChildren && (
        <div className="framework-tree-children" role="group">
          {node.children.map((child) => (
            <TreeNode
              key={child.clause.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function MappingLeaf({ row }: { row: MappingRow }) {
  const { t } = useTranslation()
  const relKey = String(row.relationshipType) as '1' | '2' | '3' | '4' | '5' | '6'
  const relName = t(`content.crosswalkRelationshipTypes.${relKey}.name`, {
    defaultValue: `Type ${row.relationshipType}`,
  })
  const relDesc = t(`content.crosswalkRelationshipTypes.${relKey}.description`, { defaultValue: '' })

  return (
    <div className="framework-tree-mapping">
      <Tooltip
        title={
          relDesc ? (
            <div style={{ fontSize: 13, lineHeight: 1.5 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{relName}</div>
              <div>{relDesc}</div>
            </div>
          ) : (
            relName
          )
        }
        placement="top"
        arrow
        slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
      >
        <span className={`crosswalk-relationship-badge crosswalk-relationship-badge--rel${relKey}`}>
          {relName}
          <HelpOutlineIcon className="crosswalk-rel-info-icon" fontSize="inherit" />
        </span>
      </Tooltip>
      <GuardedLink to={`/practice/${row.practice.id}`} className="clause-practice-link">
        <span className="clause-practice-name">{row.practice.displayName}</span>
      </GuardedLink>
      {row.note && <span className="crosswalk-note">{row.note}</span>}
    </div>
  )
}
